/**
 * PATH: packages/email-hub/src/feature/components/AiComposeButton.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-AICOMPOSEBUTTON-001
 * PURPOSE: From-scratch "Draft with AI" affordance for a NEW compose — the
 *          operator types a one-line brief, the model returns a subject + body.
 * WHY:     The reply/forward composer already had AI drafting; a blank new
 *          message did not. Batch 2 of the Composer Uplift Roadmap closes that
 *          gap without touching the (already busy) reply AI panel.
 * FLOW: click → reveal brief textarea → composeFromBrief() → onResult(subject, body)
 */

import { useState } from 'react'
import { Loader2, Send, Sparkles, X } from 'lucide-react'
import { useToast } from '../../_internal/components/Toast'
import { composeFromBrief } from '../lib/aiCompose'

export interface AiComposeButtonProps {
  /** Receives the generated subject + body. The caller decides how to apply
   *  them (e.g. only fill subject when empty, insert body at caret). */
  onResult: (result: { subject: string; body: string }) => void
  /** Optional tone passthrough (defaults to the BE's "professional"). */
  tone?: string
}

export default function AiComposeButton({ onResult, tone }: AiComposeButtonProps) {
  const [open, setOpen] = useState(false)
  const [brief, setBrief] = useState('')
  const [busy, setBusy] = useState(false)
  const { toast } = useToast()

  const generate = async () => {
    const clean = brief.trim()
    if (!clean || busy) return
    setBusy(true)
    try {
      const result = await composeFromBrief(clean, { tone })
      if (!result) {
        toast('AI could not draft from that - try a more specific brief.', 'info')
        return
      }
      onResult(result)
      setOpen(false)
      setBrief('')
    } catch (err) {
      toast(
        `AI draft failed: ${err instanceof Error ? err.message : 'unknown error'}`,
        'error',
      )
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 text-sm bg-gradient-to-r from-violet-500/10 to-purple-500/10 text-violet-700 dark:text-violet-300 rounded-lg hover:from-violet-500/20 hover:to-purple-500/20 transition-all border border-violet-200/50 dark:border-violet-800/50"
      >
        <Sparkles className="h-4 w-4" />
        Draft with AI
      </button>
    )
  }

  return (
    <div className="w-full p-3 bg-gradient-to-r from-violet-50/80 to-purple-50/50 dark:from-violet-950/20 dark:to-purple-950/10 rounded-lg border border-violet-200/50 dark:border-violet-800/40">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Draft a new email with AI
        </span>
        <button
          type="button"
          onClick={() => { setOpen(false); setBrief('') }}
          className="p-1 rounded hover:bg-accent"
          aria-label="Close AI draft"
        >
          <X className="h-3 w-3 text-muted-foreground" />
        </button>
      </div>
      <textarea
        value={brief}
        onChange={(e) => setBrief(e.target.value)}
        onKeyDown={(e) => {
          // Cmd/Ctrl+Enter generates — matches the send shortcut muscle memory.
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault()
            void generate()
          }
        }}
        rows={2}
        autoFocus
        disabled={busy}
        placeholder="What's this email about? e.g. ask Attcorp for an ETA on the beam delivery and confirm site access Friday"
        className="w-full resize-y rounded-md border border-border bg-background p-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/30"
      />
      <div className="mt-2 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => { setOpen(false); setBrief('') }}
          className="px-2.5 py-1 text-xs text-muted-foreground rounded hover:bg-accent"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void generate()}
          disabled={busy || !brief.trim()}
          className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-violet-600 text-white rounded hover:bg-violet-700 transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          {busy ? 'Drafting…' : 'Generate'}
        </button>
      </div>
    </div>
  )
}
